import React from 'react';
import PropTypes from 'prop-types';
import { FORM_ELEMENT_TYPES, VALID_FORM_ELEMENT_TYPES } from './FormElements.types';
import FormSchema from './FormElements.schema';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import useStyles from './styles';
import { DisableForRoles, HiddenForRoles } from '../../hocs';
import { DROPDOWN_NONE } from '../../utils/constants';
import { useIntl } from 'react-intl';
import { getTranslatedValue, removeSpacesFromString } from '../../utils/StringUtils';
import 'react-phone-input-2/lib/material.css'
import PhoneInput from 'react-phone-input-2'
import { IconButton, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

const FormElements = props => {
  const {
    type,
    name,
    onChange,
    otherProps,
    label,
    value,
    childValue,
    values,
    error,
    helperText,
    disabled,
    roleBasedDisable,
    allowNone,
    onKeyDown,
    transformValueBeforePrinting
  } = props;
  const classes = useStyles();
  const intl = useIntl();

  let helperTextTranslated = helperText ? getTranslatedValue(helperText, intl) : ""
  if (otherProps?.helperText) {
    otherProps.helperText = getTranslatedValue(otherProps?.helperText, intl)
  }

  switch (type) {
    case FORM_ELEMENT_TYPES.NUMBER:
    case FORM_ELEMENT_TYPES.TEXT: {
      const localOnChange = e => {
        if (type === FORM_ELEMENT_TYPES.NUMBER) {
          // replace all non numeric values with empty string
          const value = e && e.target && e.target.value ? e.target.value.replace(/[^0-9]/g, '') : '';
          onChange({ ...e, target: { ...e.target, value, name: name || '' } });
          return;
        }
        onChange(e);
      };
      return (
        <TextField
          className={classes.formElement}
          disabled={disabled || roleBasedDisable}
          error={error}
          helperText={helperTextTranslated}
          label={getTranslatedValue(label, intl)}
          name={name || ''}
          onChange={localOnChange}
          type={type === FORM_ELEMENT_TYPES.NUMBER ? 'number' : 'text'}
          value={value}
          variant="outlined"
          inputProps={{ onKeyDown: onKeyDown }}
          {...otherProps}
        />
      );
    }
    case FORM_ELEMENT_TYPES.AUTOCOMPLETE: {
      const newValues = allowNone ? [{ ...DROPDOWN_NONE }, ...values] : values;
      return (
        <Autocomplete
          id={name || ''}
          options={newValues}
          value={value}
          onChange={(e, v) => onChange({ target: { ...e.target, name, value: v } })}
          getOptionLabel={(option) => option?.label || ''}
          filterOptions={(x) => x}
          renderInput={(params) => {
            return (
              <TextField
                className={classes.formElement}
                disabled={disabled || roleBasedDisable}
                error={error}
                helperText={helperTextTranslated}
                label={getTranslatedValue(label, intl)}
                name={(name || '') + "_child"}
                // value={childValue}
                onChange={onChange}
                variant="outlined"
                inputProps={{ onKeyDown: onKeyDown }}
                {...otherProps}
                {...params}
              />)
          }}
        />
      )
    }
    case FORM_ELEMENT_TYPES.PHONE_NUMBER: {
      const localOnChange = (phoneNumber, country) => {
        const dialCode = country.dialCode
        let finalPhoneNumber = phoneNumber + ""
        if (dialCode) {
          finalPhoneNumber = finalPhoneNumber.replace(dialCode, "")
        }

        while (finalPhoneNumber.startsWith("0")) {
          finalPhoneNumber = finalPhoneNumber.substring(1)
        }

        if (dialCode) {
          finalPhoneNumber = dialCode + finalPhoneNumber
        }

        finalPhoneNumber = removeSpacesFromString(finalPhoneNumber)
        dialCode = removeSpacesFromString(dialCode)

        onChange({ target: { name, value: finalPhoneNumber + "", extraField: name + "CountryCode", extraValue: dialCode } })
      }
      return (
        <div className={classes.formElement} style={{ marginLeft: 0, marginRight: 0, width: "100%" }}>
          <PhoneInput
            className={classes.formElement}
            disabled={disabled || roleBasedDisable}
            isValid={() => {
              return !error
            }}
            label={getTranslatedValue(label, intl)}
            value={value || 0}
            onChange={localOnChange}
            preferredCountries={["il"]}
            enableSearch={true}
            disableSearchIcon={true}
            countryCodeEditable={false}
            country={"il"}
            onKeyDown={onKeyDown}
            specialLabel={getTranslatedValue(label, intl)}
            {...otherProps} />
          <Typography variant={error ? "error" : "caption"} style={{ fontSize: "min(3vw, 9px)", color: error ? "#e53935" : "#000000", margin: "0px 14px" }}>{helperTextTranslated}</Typography>
        </div>
      )
    }
    case FORM_ELEMENT_TYPES.DATE:
      let finalValue = value || "1940-01-01"
      if (transformValueBeforePrinting && typeof transformValueBeforePrinting === "function") {
        finalValue = transformValueBeforePrinting(value)
      }
      return (
        <TextField
          className={classes.formElement}
          disabled={disabled || roleBasedDisable}
          error={error}
          helperText={helperTextTranslated}
          label={getTranslatedValue(label, intl)}
          name={name || ''}
          onChange={onChange}
          type={otherProps.inputType || "datetime-local"}
          value={finalValue}
          variant="outlined"
          inputProps={{ onKeyDown: onKeyDown }}
          InputLabelProps={{
            shrink: true,
          }}
          {...otherProps}
        />
      );
    case FORM_ELEMENT_TYPES.CHECKBOX:
      return (
        <FormControlLabel
          className={classes.formElement}
          control={<Checkbox checked={!!value} disabled={disabled || roleBasedDisable} onChange={onChange} />}
          label={getTranslatedValue(label, intl)}
          name={name}
          {...otherProps}
        />
      );
    case FORM_ELEMENT_TYPES.ICON_BUTTON: {
      const { icon, ...rest } = otherProps;
      return (
        <IconButton
          className={classes.formIconButton}
          disabled={disabled || roleBasedDisable}
          onClick={() => onChange({ target: { name, value: name } })}
          {...rest}
        >
          {icon}
        </IconButton>
      );
    }
    case FORM_ELEMENT_TYPES.DROPDOWN: {
      const newValues = allowNone ? [{ ...DROPDOWN_NONE }, ...values] : values;
      const newValue = allowNone ? value || DROPDOWN_NONE.value : value;
      const newOnChange = e => {
        if (!allowNone) {
          onChange(e);
          return;
        }
        const { value } = e.target;
        const finalValue = value === DROPDOWN_NONE.value ? '' : value;
        const newEvent = { ...e };
        newEvent.target.value = finalValue;
        onChange(newEvent);
      };
      return (
        <FormControl
          className={classes.formElement}
          name={name}
          disabled={disabled || roleBasedDisable}
          error={error}
          {...otherProps}>
          <InputLabel>{getTranslatedValue(label, intl)}</InputLabel>
          <Select name={name} onChange={newOnChange} value={newValue} {...(otherProps.internalComponentProps || {})}>
            {newValues.map((val, index) => (
              <MenuItem key={`${name}#${index}`} value={val.value}>
                {getTranslatedValue(val.label, intl)}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{getTranslatedValue(label, intl)}</FormHelperText>
        </FormControl>
      );
    }
    case FORM_ELEMENT_TYPES.MULTIPLE: {
      const newValues = allowNone ? [{ ...DROPDOWN_NONE }, ...values] : values;
      let newValue = allowNone ? value || [DROPDOWN_NONE.value] : value;

      if (!Array.isArray(newValue)) {
        newValue = [newValue];
      }
      const newOnChange = e => {
        if (!allowNone) {
          onChange(e);
          return;
        }
        const { value } = e.target;
        const newValues = (value || []).filter(val => val !== DROPDOWN_NONE.value);
        const newEvent = { ...e };
        newEvent.target.value = newValues;
        onChange(newEvent);
      };
      return (
        <FormControl
          className={classes.formElement}
          name={name}
          {...otherProps}
          disabled={disabled || roleBasedDisable}
          error={error}>
          <InputLabel>{getTranslatedValue(label, intl)}</InputLabel>
          <Select multiple={true} name={name} onChange={newOnChange} value={newValue}>
            {newValues.map((val, index) => (
              <MenuItem key={`${name}#${index}`} value={val.value}>
                {val.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{getTranslatedValue(label, intl)}</FormHelperText>
        </FormControl>
      );
    }
    default:
      return null;
  }
};

FormElements.propTypes = {
  type: PropTypes.oneOf(VALID_FORM_ELEMENT_TYPES),
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  otherProps: PropTypes.object,
  value: PropTypes.any,
  values: PropTypes.array,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hiddenForRoles: PropTypes.array,
  disabledForRoles: PropTypes.array,
  allowNone: PropTypes.bool,
  onKeyDown: PropTypes.func
};

FormElements.defaultProps = {
  ...new FormSchema(FORM_ELEMENT_TYPES.GROUP, 'dummy'),
  disabled: false,
  value: '',
  error: false,
  helperText: '',
};

export default DisableForRoles(HiddenForRoles(FormElements));
